import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, subDays } from "date-fns";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import ReportsSkeleton from "@/components/organization/skeletons/ReportsSkeleton";
import ExportButton from "@/components/common/ExportButton";
import ProjectedCompensation from "@/components/organization/ProjectedCompensation";
import OrganizationReport from "@/components/organization/OrganizationReport";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn, downloadCsv, formatCompactMoney } from "@/utils/common.utils";
import { REPORT_RANGE_OPTIONS } from "@/constant/organization.constant";
import { useGetReportsSummary } from "@/hooks/useReports";
import type { DeltaTone } from "@/types/reports.types";

/** "up" always means "this got better", so the colour follows the tone the
 *  server assigned rather than the sign of the number — fewer days to close is
 *  an improvement even though the delta is negative. */
function StatTile({ label, value, delta, tone, testId }: {
  label: string;
  value: string | number;
  delta: string;
  tone: DeltaTone;
  testId: string;
}) {
  return (
    <SectionCard data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-dash-faint">{label}</p>
      <p className="mt-3 text-4xl font-bold tracking-tight text-dash-ink">{value}</p>
      <p className={cn(
        "mt-2 text-xs font-medium",
        tone === "down" ? "text-dash-rust" : tone === "up" ? "text-dash-greenMid" : "text-dash-faint",
      )}>
        {delta}
      </p>
    </SectionCard>
  );
}

function DistributionBar({ label, count, max }: { label: string; count: number; max: number }) {
  const pct = Math.max((count / max) * 100, 10);
  return (
    <div className="flex items-center gap-4">
      <span className="w-28 shrink-0 text-sm text-dash-muted">{label}</span>
      <div className="h-9 flex-1 rounded-[8px] bg-dash-chip">
        <div className="bar-fill-w flex h-full items-center overflow-hidden rounded-[8px] bg-primary px-2.5" style={{ width: `${pct}%` }}>
          <span className="text-xs font-bold text-white">{count}</span>
        </div>
      </div>
    </div>
  );
}

function WorkloadBar({ label, count, max, delta }: {
  label: string; count: number; max: number; delta: string;
}) {
  const pct = Math.max((count / max) * 100, 8);
  return (
    <div className="flex items-center gap-4">
      <span className="w-20 shrink-0 truncate text-sm text-dash-muted">{label}</span>
      <div className="h-3 flex-1 overflow-hidden rounded-full bg-dash-chip">
        <div className="bar-fill-w h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 text-xs text-dash-faint">{delta}</span>
    </div>
  );
}

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "organization", label: "Organization" },
];

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "organization" ? "organization" : "overview";
  const [range, setRange] = useState("30");

  // The range control drives a real query now. It used to be rendered and then
  // never read by anything.
  const params = useMemo(() => {
    if (range === "all") return {};
    const days = Number(range);
    return {
      from: format(subDays(new Date(), days - 1), "yyyy-MM-dd"),
      to: format(new Date(), "yyyy-MM-dd"),
    };
  }, [range]);

  const { data: summary, isLoading } = useGetReportsSummary(params);

  const distributionMax = Math.max(1, ...(summary?.distribution ?? []).map((row) => row.count));
  const workloadMax = Math.max(1, ...(summary?.workload ?? []).map((row) => row.active_deals));

  const exportCsv = () => {
    if (!summary || (!summary.distribution.length && !summary.workload.length)) {
      toast.info("There is no data to export");
      return;
    }
    downloadCsv({
      filename: "reports-summary.csv",
      rows: [
        ["Period", `${summary.period.from} to ${summary.period.to}`],
        ["Compared to", `${summary.period.compared_to.from} to ${summary.period.compared_to.to}`],
        [],
        ["Metric", "Value", "Change"],
        ["Closed deals", summary.stats.closed_deals.count, summary.stats.closed_deals.delta_label],
        ["Total volume", summary.stats.total_volume.amount, summary.stats.total_volume.delta_label],
        ["Avg days to close", summary.stats.avg_days_to_close.value ?? "—", summary.stats.avg_days_to_close.delta_label],
        ["Task completion rate", summary.stats.task_completion_rate.percent ?? "—", summary.stats.task_completion_rate.delta_label],
        [],
        ["Stage", "Deals"],
        ...summary.distribution.map((row) => [row.phase_label, row.count]),
        [],
        ["Agent", "Active deals", "Opened this period"],
        ...summary.workload.map((row) => [row.agent_name, row.active_deals, row.opened_this_period]),
      ],
    });
  };

  if (isLoading && tab === "overview") return <ReportsSkeleton />;

  const stats = summary?.stats;

  return (
    <div className="font-dashboard" data-testid="reports-page">
      <PageHeader
        title="Reports"
        description="Analyze brokerage performance across deals, agents, and teams."
        actions={tab === "overview" && (
          <>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="reports-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {REPORT_RANGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ExportButton
              onExport={exportCsv}
              label="Export CSV"
              className="h-11 !rounded-xl px-3"
              testId="reports-export-csv"
            />
          </>
        )}
      />

      <div className="mt-6 inline-flex rounded-[10px] border border-dash-border bg-white p-0.5" role="tablist" aria-label="Report views">
        {TABS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setSearchParams(value === "overview" ? {} : { tab: value }, { replace: true })}
            className={cn(
              "rounded-[8px] px-4 py-2 text-sm font-medium transition-colors",
              tab === value ? "bg-primary text-primary-foreground" : "text-dash-muted hover:text-dash-ink",
            )}
            data-testid={`reports-tab-${value}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "organization" ? <OrganizationReport /> : (<>
      <div className="mt-8 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatTile
          label="Total closed deals"
          value={stats?.closed_deals.count ?? 0}
          delta={stats?.closed_deals.delta_label ?? ""}
          tone={stats?.closed_deals.tone ?? "flat"}
          testId="reports-stat-closed"
        />
        <StatTile
          label="Total volume closed"
          value={formatCompactMoney(stats?.total_volume.amount ?? 0)}
          delta={stats?.total_volume.delta_label ?? ""}
          tone={stats?.total_volume.tone ?? "flat"}
          testId="reports-stat-volume"
        />
        <StatTile
          label="Avg. days to close"
          // null means nothing closed in the window, so there is no average to
          // show — an em dash rather than a zero, which would read as "instant".
          value={stats?.avg_days_to_close.value ?? "—"}
          delta={stats?.avg_days_to_close.delta_label ?? ""}
          tone={stats?.avg_days_to_close.tone ?? "flat"}
          testId="reports-stat-days"
        />
        <StatTile
          label="Task completion rate"
          value={stats?.task_completion_rate.percent === null || stats?.task_completion_rate.percent === undefined
            ? "—"
            : `${stats.task_completion_rate.percent}%`}
          delta={stats?.task_completion_rate.delta_label ?? ""}
          tone={stats?.task_completion_rate.tone ?? "flat"}
          testId="reports-stat-tasks"
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Deal distribution"
          data-testid="reports-distribution"
        >
          {summary?.distribution.length ? (
            <div className="space-y-3">
              {summary.distribution.map((row) => (
                <DistributionBar key={row.phase} label={row.phase_label} count={row.count} max={distributionMax} />
              ))}
            </div>
          ) : (
            <EmptyState compact title="No deals in this period" description="Widen the date range to see how work is distributed across stages." className="border-0" />
          )}
        </SectionCard>

        <SectionCard
          title="Agent workload"
          data-testid="reports-workload"
        >
          {summary?.workload.length ? (
            <>
              <div className="space-y-3">
                {summary.workload.map((row) => (
                  <WorkloadBar
                    key={row.agent_id ?? row.agent_name}
                    label={row.agent_name}
                    count={row.active_deals}
                    max={workloadMax}
                    delta={row.delta_label}
                  />
                ))}
              </div>
              <p className="mt-5 text-xs text-dash-faint">Bar length is each agent&apos;s open caseload; the figure beside it is how much of that they opened in this period.</p>
            </>
          ) : (
            <EmptyState compact title="No active deals" description="Agent caseloads appear here once deals are in flight." className="border-0" />
          )}
        </SectionCard>
      </div>

      {/* Its own month picker: this looks ahead by closing date, while the range above looks back. */}
      <div className="mt-4">
        <ProjectedCompensation />
      </div>
      </>)}
    </div>
  );
}
